import { zodResolver } from '@hookform/resolvers/zod'
import {
  type CreateTrainingSkillItemInput,
  CreateTrainingSkillItemSchema,
  TRAINING_SKILL_ITEM_CSV_TEMPLATE_HEADERS,
  type TrainingSkillItemImportResult,
} from '@lms/shared'
import { AlertCircle, ArrowLeft, FileUp, Plus } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { Link, useParams } from 'react-router-dom'
import { toast } from 'sonner'
import { SkillItemEditDialog } from '@/components/training/SkillItemEditDialog'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { Switch } from '@/components/ui/switch'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Textarea } from '@/components/ui/textarea'
import {
  useCreateTrainingSkillItem,
  useImportTrainingSkillItems,
  useTrainingSkillItems,
  useUpdateTrainingSkillItem,
} from '@/hooks/use-training-skill-items'
import { useTraining, useUpdateTraining } from '@/hooks/use-trainings'
import { readCsvFile } from '@/lib/csv'
import { useTenantPath } from '@/lib/tenant'

const errorMessage = (err: unknown, fallback: string) =>
  err instanceof Error ? err.message : fallback

function SkillCheckNoteCard({
  trainingId,
  savedNote,
}: {
  trainingId: string
  savedNote: string | null
}) {
  const updateTraining = useUpdateTraining()
  const [note, setNote] = useState(savedNote ?? '')

  useEffect(() => setNote(savedNote ?? ''), [savedNote])

  const handleSave = async () => {
    try {
      await updateTraining.mutateAsync({
        id: trainingId,
        data: { skillCheckNote: note.trim() || null },
      })
      toast.success('補足を保存しました')
    } catch (err) {
      toast.error(errorMessage(err, '保存に失敗しました'))
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">評価シートの補足</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-muted-foreground">
          評価の目安などを、受講者と上司の評価シートの評価基準欄に表示します。
        </p>
        <Textarea
          placeholder="※基礎編受講者は6項目目までのクリアが目安"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          maxLength={500}
          rows={2}
        />
        <Button
          size="sm"
          onClick={handleSave}
          disabled={note === (savedNote ?? '') || updateTraining.isPending}
        >
          {updateTraining.isPending ? '保存中...' : '保存'}
        </Button>
      </CardContent>
    </Card>
  )
}

function SkillItemCreateCard({ trainingId }: { trainingId: string }) {
  const createItem = useCreateTrainingSkillItem(trainingId)
  const form = useForm<CreateTrainingSkillItemInput>({
    resolver: zodResolver(CreateTrainingSkillItemSchema),
    defaultValues: { code: '', category: '', label: '' },
  })

  const onSubmit = async (values: CreateTrainingSkillItemInput) => {
    try {
      await createItem.mutateAsync(values)
      toast.success('項目を追加しました')
      form.reset({ code: '', category: values.category, label: '' })
    } catch (err) {
      toast.error(errorMessage(err, '追加に失敗しました'))
    }
  }

  const fields = [
    { name: 'code', label: '項目コード', placeholder: 'LT101' },
    { name: 'category', label: '大項目', placeholder: '■論理的思考を意識する' },
    {
      name: 'label',
      label: '具体項目',
      placeholder: '感情や直感ではなく、論理に基づいて判断している',
    },
  ] as const

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">項目を追加</CardTitle>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-3">
            <div className="grid gap-3 md:grid-cols-[8rem_1fr_2fr]">
              {fields.map(({ name, label, placeholder }) => (
                <FormField
                  key={name}
                  control={form.control}
                  name={name}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{label}</FormLabel>
                      <FormControl>
                        <Input placeholder={placeholder} {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              ))}
            </div>
            <p className="text-xs text-muted-foreground">
              項目コードは追加後に変更できません（集計で同じ項目として扱うため）。
            </p>
            <Button type="submit" size="sm" disabled={createItem.isPending}>
              <Plus className="mr-1 h-4 w-4" />
              追加
            </Button>
          </form>
        </Form>
      </CardContent>
    </Card>
  )
}

function SkillItemImportCard({ trainingId }: { trainingId: string }) {
  const importItems = useImportTrainingSkillItems(trainingId)
  const [result, setResult] = useState<TrainingSkillItemImportResult | null>(null)
  const fileRef = useRef<HTMLInputElement>(null)

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return
    if (
      !confirm(
        'CSVの内容を正として項目を同期します。CSVに無い項目は無効化されます（評価中の受講者の項目は変わりません）。よろしいですか？',
      )
    ) {
      return
    }

    try {
      const res = await importItems.mutateAsync(await readCsvFile(file))
      setResult(res)
      if (res.imported) {
        toast.success(
          `取り込みました（追加 ${res.created} 件・更新 ${res.updated} 件・無効化 ${res.deactivated} 件）`,
        )
      }
    } catch (err) {
      setResult(null)
      toast.error(errorMessage(err, '取り込みに失敗しました'))
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">CSVから一括取り込み（任意）</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-muted-foreground">
          見出し行: <code>{TRAINING_SKILL_ITEM_CSV_TEMPLATE_HEADERS.join(',')}</code>
          （「項目ID」「項目名」でも可）。行の順番が表示順になります。
        </p>
        <input
          ref={fileRef}
          type="file"
          accept=".csv,.tsv,.txt"
          className="hidden"
          onChange={handleFile}
        />
        <Button
          variant="outline"
          onClick={() => fileRef.current?.click()}
          disabled={importItems.isPending}
        >
          <FileUp className="mr-1 h-4 w-4" />
          {importItems.isPending ? '取り込み中...' : 'CSVファイルを選択'}
        </Button>

        {result && !result.imported && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              <p>エラーがあるため取り込みませんでした。</p>
              <ul className="mt-1 list-disc pl-4">
                {result.errors.map((error) => (
                  <li key={`${error.row}-${error.message}`}>
                    {error.row}行目: {error.message}
                  </li>
                ))}
              </ul>
            </AlertDescription>
          </Alert>
        )}
      </CardContent>
    </Card>
  )
}

export default function TrainingSkillItemsPage() {
  const { id = '' } = useParams<{ id: string }>()
  const tp = useTenantPath()
  const { data } = useTraining(id)
  const { data: items = [], isLoading } = useTrainingSkillItems(id)
  const updateItem = useUpdateTrainingSkillItem(id)

  const training = data as { title?: string; skillCheckNote?: string | null } | undefined

  const handleToggleActive = async (itemId: string, isActive: boolean) => {
    try {
      await updateItem.mutateAsync({ itemId, data: { isActive } })
    } catch (err) {
      toast.error(errorMessage(err, '更新に失敗しました'))
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Link to={tp('/teacher/trainings')}>
          <Button variant="ghost" size="sm">
            <ArrowLeft className="mr-1 h-4 w-4" />
            戻る
          </Button>
        </Link>
        <h1 className="text-2xl font-bold">スキルチェック項目</h1>
      </div>

      <p className="text-muted-foreground">研修: {training?.title}</p>

      <SkillItemCreateCard trainingId={id} />

      <Card>
        <CardHeader>
          <CardTitle className="text-base">項目一覧 ({items.length}件)</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <Skeleton className="h-40" />
          ) : items.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">
              項目が登録されていません。項目が無い研修ではフォローアップ（スキルチェック）は作成されません。
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-28">項目コード</TableHead>
                  <TableHead>大項目</TableHead>
                  <TableHead>具体項目</TableHead>
                  <TableHead className="w-20 text-center">有効</TableHead>
                  <TableHead className="w-16 text-center">編集</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((item) => (
                  <TableRow key={item.id} className={item.isActive ? undefined : 'opacity-50'}>
                    <TableCell className="font-mono text-sm">{item.code}</TableCell>
                    <TableCell className="text-sm">{item.category}</TableCell>
                    <TableCell className="text-sm">{item.label}</TableCell>
                    <TableCell className="text-center">
                      <Switch
                        checked={item.isActive}
                        onCheckedChange={(checked) => handleToggleActive(item.id, checked)}
                        disabled={updateItem.isPending}
                        aria-label={`${item.code} を${item.isActive ? '無効化' : '有効化'}`}
                      />
                    </TableCell>
                    <TableCell className="text-center">
                      <SkillItemEditDialog trainingId={id} item={item} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <SkillCheckNoteCard trainingId={id} savedNote={training?.skillCheckNote ?? null} />

      <SkillItemImportCard trainingId={id} />
    </div>
  )
}
